import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CriminalDetail } from "../../interfaces/criminalDetail";
import {
  createCriminalDetail,
  deleteCriminalDetail,
  deleteCriminalDetailForm,
  getCriminalDetailByID,
  updateCriminalDetail,
  uploadReceivedForm,
} from "../../services/criminalDetailService";
import { formatDate, sortServices } from "../../services/helpers";
import { HttpService } from "../../services/httpService";
import { CriminalOrder } from "../../interfaces/criminalOrder";
import { getCriminalOrderByID } from "../../services/criminalOrderService";
import { getVendors } from "../../services/vendorService";
import { Vendor } from "../../interfaces/vendor";
import { Service } from "../../interfaces/service";
import { getAllServices } from "../../services/serviceService";
import ToolTip from "../../components/component-tools/ToolTip";
import NotifyClient from "../../components/order-components/NotifyClient";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import { useViewOrderContext } from "../../contexts/ViewOrderContext";
import { getSimpleOrderByID } from "../../services/orderService";
import { SimpleOrder } from "../../interfaces/simpleOrder";
import { NotificationType } from "../../interfaces/clientNotification";
import { getAdditionalNamesByOrderID } from "../../services/additionalNameService";
import { AdditionalName } from "../../interfaces/additionalName";

const OrderService = () => {
  const [criminalDetail, setCriminalDetail] = useState<CriminalDetail | null>(
    null,
  );
  const [criminalOrder, setCriminalOrder] = useState<CriminalOrder | null>(
    null,
  );
  const [order, setOrder] = useState<SimpleOrder | null>(null);
  const { state } = useLocation();
  var criminalOrderID = state?.criminalOrderID || null;
  const criminalDetailID = state?.criminalDetailID || null;
  const closed = state?.closed || null;
  // const [file, setFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [receivedFormUrl, setReceivedFormUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>();
  const [services, setServices] = useState<Service[]>();
  const [isFeeManuallySet, setIsFeeManuallySet] = useState(false);
  const [showNotifyClientPopup, setShowNotifyClientPopup] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationForm, setShowConfirmationForm] = useState(false);
  const { setCriminalDetails, criminalDetails } = useViewOrderContext();
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [initiatedLocked, setInitiatedLocked] = useState(false);
  const [famaVendor, setFamaVendor] = useState(false);
  const [editing, setEditing] = useState(false);
  const [additionalNames, setAdditionalNames] = useState<AdditionalName[]>();

  const statusClassMap: Record<number, string> = {
    1: "status-pending",
    2: "status-admin-review",
    3: "status-in-process",
    4: "status-completed",
    5: "status-deleted",
    6: "status-subscription",
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchCriminalOrderDetails = async () => {
      try {
        if (criminalDetailID) {
          const id = parseInt(criminalDetailID, 10);
          const response = await getCriminalDetailByID(id);
          setCriminalDetail(response);

          criminalOrderID = response.criminalOrderID;

          var locked =
            response.vendorID === 2 ||
            response.vendorID === 4 ||
            response.vendorID === 5 ||
            response.vendorID === 13;
          setInitiatedLocked(locked);

          var famaVendorResponse = response.vendorID === 13;
          setFamaVendor(famaVendorResponse);

          //get received form url
          try {
            if (response?.receivedFormUrl) {
              const signedCriminalUrl = await HttpService.getFileAdmin(
                "criminal-detail-forms",
                response.criminalDetailID.toString(),
                response.receivedFormUrl,
              );
              setReceivedFormUrl(signedCriminalUrl);
            }
          } catch (error) {
            console.error(
              `Failed to fetch signed URL for received form`,
              error,
            );
          }
        } else {
          setCriminalDetail({
            criminalDetailID: 0,
            criminalOrderID: criminalOrderID,
            serviceID: 4,
            vendorID: 5,
            initiatedDate: undefined,
            receivedDate: undefined,
            resultsFieldOne: "",
            resultsFieldTwo: "",
            fee: 0,
            redFlag: false,
            comments: "",
            receivedFormUrl: "",
            hasResponse: false,
            adminReviewed: false,
            subscriptionAddOn: false,
          });
        }
      } catch (err) {
        console.log("Failed to fetch criminal detail.");
      } finally {
        setLoadingSpinner(false);
      }

      // get criminal order, order and additional names
      try {
        const criminalOrderResponse =
          await getCriminalOrderByID(criminalOrderID);
        setCriminalOrder(criminalOrderResponse);

        const orderResponse = await getSimpleOrderByID(
          criminalOrderResponse.orderID,
        );
        setOrder(orderResponse);

        const addNames = await getAdditionalNamesByOrderID(
          orderResponse.orderID,
        );
        setAdditionalNames(addNames);
      } catch {
        console.log("Criminal Order ID not found.");
      }

      // get all vendors
      const vendorResponse = await getVendors();
      const sortedVendors = vendorResponse.filter((vendor) => vendor.isActive);
      setVendors(sortedVendors);

      // get all services
      const serviceResponse = await getAllServices();
      const filteredServices = serviceResponse.filter(
        (service) =>
          !service.serviceName.toLowerCase().includes("referencing") &&
          service.serviceID !== 30 &&
          service.serviceID !== 31 &&
          service.serviceID !== 32 &&
          !service.subscriptionService,
      );
      const sortedServices = await sortServices(filteredServices);
      setServices(sortedServices);
    };

    fetchCriminalOrderDetails();
  }, [criminalDetailID, criminalOrderID]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (
      criminalDetail?.serviceID &&
      services &&
      !isFeeManuallySet &&
      !criminalDetailID
    ) {
      const selectedService = services.find(
        (service) => service.serviceID === Number(criminalDetail.serviceID),
      );

      if (selectedService) {
        setCriminalDetail((prev) => ({
          ...prev!,
          fee: selectedService.fee,
          vendorID: selectedService.vendorID,
        }));
      }
    }
  }, [criminalDetail?.serviceID, services, criminalDetailID, isFeeManuallySet]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: keyof CriminalDetail,
  ) => {
    setEditing(true);
    let value: string | boolean | null =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (field === "fee") {
      setIsFeeManuallySet(true);
    }

    if (field === "vendorID") {
      var locked =
        value === "2" || value === "4" || value === "5" || value === "13";
      setInitiatedLocked(locked);

      var famaLockedResult = value === "13";
      setFamaVendor(famaLockedResult);
    }

    // handle date clearing safely
    if (field === "initiatedDate") {
      value = e.target.value === "" ? null : e.target.value;
    }

    if (criminalDetail) {
      if (field === "serviceID" && value === "5") {
        setCriminalDetail({
          ...criminalDetail,
          [field]: parseInt(value, 10),
          resultsFieldTwo: "Statewide",
        });
      } else if (field === "subscriptionAddOn") {
        setCriminalDetail({
          ...criminalDetail,
          subscriptionAddOn: value as boolean,
          fee: 0,
        });
      } else {
        setCriminalDetail({
          ...criminalDetail,
          [field]: value,
        });
      }
    }
  };

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setIsFeeManuallySet(false);
    handleInputChange(e, "serviceID");
  };

  const handleFormUpload = async () => {
    if (criminalDetail?.criminalDetailID) {
      //save right away
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";
      fileInput.click();
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
          setLoadingSpinner(true);
          const response = await uploadReceivedForm(
            file,
            criminalDetail.criminalDetailID,
          );

          if (response.receivedFormUrl) {
            const signed = await HttpService.getFileAdmin(
              "criminal-detail-forms",
              response.criminalDetailID.toString(),
              response.receivedFormUrl,
            );
            setReceivedFormUrl(signed);
          }

          const updated: CriminalDetail = {
            ...criminalDetail,
            receivedFormUrl: response.receivedFormUrl,
            hasResponse: true,
          };

          setCriminalDetail(updated);

          setCriminalDetails((prev) =>
            prev.map((cd) =>
              cd.criminalDetailID === updated.criminalDetailID ? updated : cd,
            ),
          );

          await updateCriminalDetail(updated);

          setSuccessMessage("Received form uploaded successfully");
        } catch (error) {
          console.error("Error handling file upload:", error);
        } finally {
          setShowUploadConfirmation(false);
          setLoadingSpinner(false);
        }
      };
    } else {
      //store til final save
      try {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/*,.pdf";
        fileInput.click();
        fileInput.onchange = async (event: any) => {
          const file = event.target.files[0];
          if (!file) return;
          // setFile(file);
          setSuccessMessage("Received form uploaded successfully");
        };
        setEditing(true);
      } catch (error) {
        console.error("Error handling file upload:", error);
      } finally {
        setLoadingSpinner(false);
      }
    }
  };

  const handleSave = async () => {
    setLoadingSpinner(true);

    try {
      if (
        criminalDetail &&
        criminalDetail.criminalDetailID === 0 &&
        criminalOrder
      ) {
        // new criminal detail and possibly response file
        const result = await createCriminalDetail(criminalDetail);
        setCriminalDetail(result);

        setCriminalDetails([...criminalDetails, result]);

        navigate(`/admin/view-order/${criminalOrder.orderID}`, {
          state: {
            successMessage: "Criminal Detail created successfully.",
          },
        });
      } else if (criminalDetail && criminalDetail.criminalDetailID !== 0) {
        // update criminal detail

        // set received date correctly after clear
        const payload: any = { ...criminalDetail };
        if (payload.receivedDate === "") {
          payload.receivedDate = null;
        }

        const result = await updateCriminalDetail(payload as CriminalDetail);
        setCriminalDetail(result);

        setCriminalDetails((prev) =>
          prev.map((cd) =>
            cd.criminalDetailID === result.criminalDetailID ? result : cd,
          ),
        );
        setSuccessMessage("Updated Successfully.");
        setEditing(false);
      }
    } catch (error) {
      console.error("Error saving criminal details:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (criminalDetailID && criminalDetail && criminalOrder) {
        await deleteCriminalDetail(criminalDetail?.criminalDetailID);

        setCriminalDetails(
          criminalDetails.filter(
            (cd) => cd.criminalDetailID !== criminalDetail.criminalDetailID,
          ),
        );

        navigate(`/admin/view-order/${criminalOrder.orderID}`, {
          state: {
            successMessage: "Criminal Detail deleted successfully.",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting criminal detail:", error);
      setSuccessMessage("");
    }
  };

  const handleNotifyClientClosePopup = (message?: string) => {
    if (message) {
      window.location.reload();
    }
    setShowNotifyClientPopup(false);
  };

  const handleDeleteForm = async () => {
    try {
      setLoadingSpinner(true);
      if (criminalDetailID && criminalDetail && criminalOrder) {
        await deleteCriminalDetailForm(criminalDetail?.criminalDetailID);

        const updated: CriminalDetail = {
          ...criminalDetail,
          receivedFormUrl: "",
          hasResponse: false,
        };

        setCriminalDetail(updated);
        setReceivedFormUrl("");

        setCriminalDetails((prev) =>
          prev.map((cd) =>
            cd.criminalDetailID === updated.criminalDetailID ? updated : cd,
          ),
        );

        setSuccessMessage("Form deleted successfully.");
      }
      setShowConfirmationForm(false);
    } catch (error) {
      console.error("Error deleting criminal detail form:", error);
      setSuccessMessage("");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleFormUploadClicked = async () => {
    if (criminalDetail?.hasResponse) {
      setShowUploadConfirmation(true);
    } else {
      handleFormUpload();
    }
  };

  return (
    <div className="content-container">
      <div className="data-container">
        <div
          className="page-items-container"
          style={{
            alignItems: "center",
            flex: "0 0 100%",
            justifyContent: "space-between",
          }}
        >
          <h1>
            {criminalDetailID
              ? "View Criminal Service"
              : "Create Criminal Service"}
          </h1>
          {order !== null && (
            <div
              className="page-items-container"
              style={{ alignItems: "center" }}
            >
              <h3
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/admin/view-order/${order.orderID}`)}
              >
                {order?.candidateFirstName + " " + order?.candidateLastName}
              </h3>
              <div
                className={`chip-date ${
                  statusClassMap[order?.orderStatusID ?? 1] || "status-unknown"
                }`}
                style={{
                  gap: "10px",
                  justifyContent: "end",
                }}
              >
                <div>
                  {order.orderStatusID === 2
                    ? "Admin Review"
                    : order.orderStatusID === 3
                      ? "In Process"
                      : order.orderStatusID === 4
                        ? "Completed"
                        : order.orderStatusID === 5
                          ? "Deleted"
                          : order.orderStatusID === 6
                            ? "Subscription"
                            : "Pending"}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="divider-small" style={{ margin: "0px" }}></div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Service:</label>
          <div
            style={{
              flex: "0 0 30%",
              alignSelf: "end",
              display: "flex",
              flexDirection: "row",
            }}
          >
            {services && (
              <select
                className={`form-select`}
                value={criminalDetail?.serviceID || ""}
                onChange={(e) => handleServiceChange(e)}
                disabled={
                  closed ||
                  (initiatedLocked &&
                    criminalDetail?.initiatedDate !== null &&
                    criminalDetail?.initiatedDate !== undefined)
                }
              >
                {services.map((service) => (
                  <option key={service.serviceID} value={service.serviceID}>
                    {service.serviceName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Vendor:</label>
          <div
            style={{
              flex: "0 0 30%",
              alignSelf: "end",
              display: "flex",
              flexDirection: "row",
            }}
          >
            {vendors && (
              <select
                className={`form-select`}
                value={criminalDetail?.vendorID || ""}
                onChange={(e) => handleInputChange(e, "vendorID")}
                disabled={
                  closed ||
                  (initiatedLocked &&
                    criminalDetail?.initiatedDate !== null &&
                    criminalDetail?.initiatedDate !== undefined)
                }
              >
                {vendors.map((vendor) => (
                  <option key={vendor.vendorID} value={vendor.vendorID}>
                    {vendor.vendorName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Initiated: </label>
            <ToolTip
              tooltipText="The Initiated Date is system-generated when the order is processed in the vendor table. To prevent the order from being sent, manually enter a date."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>

          <div className="field-input">
            <input
              className="form-control"
              type="date"
              value={
                criminalDetail?.initiatedDate !== undefined
                  ? formatDate(criminalDetail?.initiatedDate)
                  : ""
              }
              onChange={(e) => handleInputChange(e, "initiatedDate")}
              disabled={initiatedLocked}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Received: </label>
            <ToolTip
              tooltipText="When this service request is returned from the vendor."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>
          <div className="field-input">
            <input
              className="form-control"
              type="date"
              value={
                criminalDetail?.receivedDate !== undefined
                  ? formatDate(criminalDetail?.receivedDate)
                  : ""
              }
              onChange={(e) => handleInputChange(e, "receivedDate")}
              disabled={!criminalDetailID}
            />
          </div>
        </div>
        {additionalNames && additionalNames?.length > 0 && (
          <div className="error" style={{ alignSelf: "center" }}>
            {additionalNames?.length} ADDITIONAL NAME(S)
          </div>
        )}

        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Field One:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={criminalDetail?.resultsFieldOne || ""}
              onChange={(e) => handleInputChange(e, "resultsFieldOne")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Field Two:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={criminalDetail?.resultsFieldTwo || ""}
              onChange={(e) => handleInputChange(e, "resultsFieldTwo")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 10%" }}>
          <label className="field-label">Fee:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={criminalDetail?.fee || ""}
              onChange={(e) => handleInputChange(e, "fee")}
              disabled={criminalDetail?.subscriptionAddOn}
            />
          </div>
        </div>
        {showNotifyClientPopup && criminalDetail && criminalOrder && order && (
          <NotifyClient
            orderId={order?.orderID}
            clientId={order.clientID}
            detailId={criminalDetail.criminalDetailID}
            candidateEmail={order.candidateEmail}
            type={NotificationType.Subscription}
            onClose={handleNotifyClientClosePopup}
          />
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div className="field-stacked">
              <label className="field-label">Red Flag:</label>
              <div>
                <input
                  className="form-check-input check-red"
                  type="checkbox"
                  checked={
                    criminalDetail?.redFlag ? criminalDetail?.redFlag : false
                  }
                  onChange={(e) => handleInputChange(e, "redFlag")}
                />
              </div>
            </div>
            {/* only display admin reviewed if detail is a fama service or is a subscription add-on */}
            {(famaVendor || criminalDetail?.subscriptionAddOn) && (
              <div className="field-stacked">
                <div style={{ display: "flex", gap: "5px" }}>
                  <label className="field-label">Admin Reviewed:</label>
                  <ToolTip
                    tooltipText="Check when the response from the vendor has been reviewed. This will remove it from the 'Completed Vendor Orders' list. It will also allow subscription add-on services to be viewed by client."
                    placement="right"
                    className="info-icon-spec"
                  ></ToolTip>
                </div>
                <div>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={
                      criminalDetail?.adminReviewed
                        ? criminalDetail?.adminReviewed
                        : false
                    }
                    onChange={(e) => handleInputChange(e, "adminReviewed")}
                    disabled={!criminalDetailID}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
              gap: "20px",
            }}
          >
            <div className="field-stacked">
              <label className="field-label">Subscription Add-On:</label>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={
                    criminalDetail?.subscriptionAddOn
                      ? criminalDetail?.subscriptionAddOn
                      : false
                  }
                  onChange={(e) => handleInputChange(e, "subscriptionAddOn")}
                />
              </div>
            </div>
            {criminalDetail?.subscriptionAddOn && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "20px" }}
              >
                <button
                  className={`smbtn btn-grey ${
                    !criminalDetailID || criminalDetail?.clientNotifiedDate
                      ? "disabled"
                      : ""
                  }`}
                  onClick={() =>
                    setShowNotifyClientPopup(!showNotifyClientPopup)
                  }
                  disabled={
                    !criminalDetailID ||
                    criminalDetail?.clientNotifiedDate != null
                  }
                >
                  Notify Client <i className="btn-icon bi bi-envelope"></i>
                </button>
                {criminalDetail?.clientNotifiedDate && (
                  <div className="field-long" style={{ flex: "0 0 20%" }}>
                    <label className="field-label">Client Notified: </label>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="date"
                        value={
                          criminalDetail?.clientNotifiedDate !== undefined
                            ? formatDate(criminalDetail?.clientNotifiedDate)
                            : ""
                        }
                        disabled={true}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Comments:</label>
          <div className="field-input">
            <textarea
              className="form-control"
              value={criminalDetail?.comments || ""}
              onChange={(e) => handleInputChange(e, "comments")}
              style={{ height: "100px" }}
              maxLength={2000}
            />
            <div
              style={{
                fontSize: "14px",
                marginTop: "4px",
                textAlign: "right",
              }}
            >
              {criminalDetail?.comments?.length ?? 0}/2000
            </div>
          </div>
        </div>

        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Mark as inactive:</label>
            <ToolTip
              tooltipText="Please check this box if you want to exclude this order from invoicing and the final report. (ex. re-ordering from another vendor) or if subscription add-on resulted in no record match for the candidate."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>
          <div>
            <input
              className="form-check-input"
              type="checkbox"
              checked={
                criminalDetail?.inActive ? criminalDetail?.inActive : false
              }
              onChange={(e) => handleInputChange(e, "inActive")}
              disabled={!criminalDetailID}
            />
          </div>
        </div>

        {receivedFormUrl && !criminalDetail?.subscriptionAddOn && (
          <div className="error" style={{ flex: "0 0 100%" }}>
            All uploads will be included in the final client report{" "}
          </div>
        )}

        {criminalDetailID && (
          <button
            className="smbtn btn-grey"
            onClick={() => handleFormUploadClicked()}
          >
            Upload Form
          </button>
        )}
        {receivedFormUrl && (
          <div
            style={{
              display: "flex",
              height: "max-content",
              gap: "10px",
            }}
          >
            <button
              className="smbtn btn-purple"
              onClick={() => {
                if (receivedFormUrl) {
                  window.open(receivedFormUrl, "_blank");
                }
              }}
            >
              View Received Form
              <i className="btn-icon bi bi-box-arrow-up-right"></i>
            </button>
            <div onClick={() => setShowConfirmationForm(true)}>
              <i
                className="bi bi-x-lg"
                style={{ color: "red", cursor: "pointer" }}
              ></i>
            </div>
          </div>
        )}
        {showUploadConfirmation && (
          <ConfirmDelete
            onConfirm={() => handleFormUpload()}
            onCancel={() => {
              setShowUploadConfirmation(false);
            }}
            message="There is a form uploaded, you will be replacing it."
          />
        )}
        {showConfirmationForm && (
          <ConfirmDelete
            onConfirm={() => handleDeleteForm()}
            onCancel={() => setShowConfirmationForm(false)}
            message="Delete this form?"
          />
        )}

        <div style={{ flex: "0 0 100%" }}>
          {successMessage && (
            <div className=" alert alert-success fade show">
              {successMessage}
            </div>
          )}
        </div>
      </div>

      <div className="action-buttons">
        <button
          className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
          onClick={() => handleSave()}
          disabled={!editing}
        >
          Save
        </button>
        {(criminalDetail?.initiatedDate == null ||
          (criminalDetail?.vendorID !== 2 && criminalDetail?.vendorID !== 5)) &&
          criminalDetailID &&
          !closed && (
            <button
              className="oxbtn btn-red"
              onClick={() => setShowConfirmation(true)}
            >
              Delete
            </button>
          )}
        {showConfirmation && (
          <ConfirmDelete
            onConfirm={() => handleDelete()}
            onCancel={() => setShowConfirmation(false)}
            message="Are you sure you want to delete this service?"
          />
        )}
      </div>

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default OrderService;
